import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import PageImageSrc from "../../public/brand/background.png";
import BrandImageSrc from "../../public/brand/Logo on White@4x.png";
import BarlowLight from "../../public/font/Barlow-Light.ttf";
import BarlowBold from "../../public/font/Barlow-Bold.ttf";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => void;
  }
}

interface TrackMasterDataItem {
  ip: string;
  host: string;
  country: string;
  ipDetails: {
    response: string;
    country_code: string;
    country_name: string;
    region_name: string;
    city_name: string;
    latitude: number;
    longitude: number;
    zip_code: string;
    time_zone: string;
    isp: string;
    domain: string;
    net_speed: string;
    idd_code: string;
    area_code: string;
    weather_station_code: string;
    weather_station_name: string;
    mcc: string;
    mnc: string;
    mobile_brand: string;
    elevation: number;
    usage_type: string;
    address_type: string;
    category: string;
    category_name: string;
    credits_consumed: number;
  };
}

interface GeneratePdfOptions {
  dataItem: TrackMasterDataItem;
  sessionId: string;
  clientFullName?: string;
  clientEmail?: string;
}

interface GenerateMultiPdfOptions {
  dataItems: TrackMasterDataItem[];
  sessionId: string;
  clientFullName?: string;
  clientEmail?: string;
}

const printJustifiedLine = (
  doc: jsPDF,
  line: string,
  x: number,
  y: number,
  maxWidth: number,
  isLastLine: boolean
): void => {
  const words = line.split(" ");
  let currentX = x;

  const spaceWidth = isLastLine
    ? doc.getTextWidth(" ")
    : (maxWidth -
        words.reduce((width, word) => width + doc.getTextWidth(word), 0)) /
      (words.length - 1);

  words.forEach((word, index) => {
    doc.text(word, currentX, y);
    if (index < words.length - 1) {
      currentX += doc.getTextWidth(word) + spaceWidth;
    }
  });
};

const justifyText = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number
): number => {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  const lineHeight = 10;

  words.forEach((word) => {
    const testLine = line + word + " ";
    const testLineWidth = doc.getTextWidth(testLine);

    if (testLineWidth > maxWidth && line !== "") {
      lines.push(line.trim());
      line = word + " ";
    } else {
      line = testLine;
    }
  });

  if (line) {
    lines.push(line.trim());
  }

  lines.forEach((currLine, index) => {
    const isLastLine = index === lines.length - 1;
    printJustifiedLine(
      doc,
      currLine,
      x,
      y + index * lineHeight,
      maxWidth,
      isLastLine
    );
  });

  return y + lines.length * lineHeight;
};

export const generateTrackMasterPDF = ({
  dataItem,
  clientFullName,
  clientEmail,
}: Omit<GeneratePdfOptions, 'sessionId'>): jsPDF => {
  let maxWidth = 320;
  let text = "";

  const pageImage = new Image();
  pageImage.src = PageImageSrc;
  const brandPage = new Image();
  brandPage.src = BrandImageSrc;

  applyPlugin(jsPDF);
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const barlowLight = BarlowLight;
  const barlowBold = BarlowBold;
  doc.addFont(barlowLight, "Barlow", "light");
  doc.addFont(barlowBold, "Barlow", "bold");
  let yPosition = pageHeight / 5;
  const xPosition = 25;

  for (let i = 0; i < 10; i++) {
    doc.addImage(pageImage, pageWidth - 60, 0, 225 / 3, 389 / 3);
    doc.addImage(brandPage, 20, 15, 829 / 18, 332 / 18);
    text = "support@onlinejustice.com";
    const text_width = doc.getTextWidth(text);
    doc.setFont("Barlow", "light");
    doc.text(text, xPosition, pageHeight - 12);
    doc.setDrawColor(211, 167, 108);
    doc.line(
      text_width + xPosition + 12,
      pageHeight - 12,
      pageWidth,
      pageHeight - 12
    );
    doc.addPage();
  }
  doc.deletePage(11);
  doc.setPage(1);
  doc.setFont("Barlow", "light");
  maxWidth = 120;
  text =
    "The report contains information about the device used by the scammer, including the IP address, device information, timezone, and location details. This information can be used to identify the scammer and potentially recover the funds.";
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 10;
  doc.setFontSize(24);
  doc.setFont("Barlow", "bold");
  doc.text("Report: Scam Investigation Findings", xPosition, yPosition + 20);
  yPosition += 18;
  doc.setFontSize(20);
  doc.text(`for ${clientFullName || clientEmail || "Client"}`, xPosition, yPosition + 12);
  doc.setPage(2);
  doc.setFontSize(24);
  yPosition = pageHeight / 5 - 12;
  doc.setFont("Barlow", "bold");
  doc.text("Introduction", xPosition, yPosition);
  yPosition += 18;
  doc.setFontSize(14);
  doc.setFont("Barlow", "light");
  text = `This report presents the findings of the investigation into a scam targeting ${clientFullName || clientEmail || "the client"}. The investigation aims to identify the perpetrators involved in the scam and gather relevant information that can assist in the recovery of funds. The following report provides a detailed analysis of the available data and technical information obtained from the IP address used during the fraudulent activity.`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 36;
  doc.setFillColor(242, 242, 242);
  doc.text("Investigation Details:", xPosition, yPosition + 12);
  doc.text("Device Information", xPosition, yPosition + 24);
  yPosition += 32;
  const columns = ["Information", "Value"];

  let data = [
    ["IP address", dataItem.ip],
    ["Device", dataItem.host],
  ];
  doc.autoTable({
    columns: columns,
    body: data,
    margin: { left: xPosition },
    startY: yPosition,
    tableWidth: maxWidth,
    styles: {
      fillColor: [220, 220, 220],
    },
    headStyles: {
      fillColor: [50, 50, 50],
      fontSize: 14,
    },
    bodyStyles: {
      fillColor: [220, 220, 220],
      fontSize: 12,
    },
  });
  yPosition += 18;
  doc.setPage(3);
  yPosition = pageHeight / 5 - 20;
  doc.text("Location Information IP2", xPosition, yPosition);
  yPosition += 12;
  data = [
    ["Response", dataItem.ipDetails.response],
    ["Country Code", dataItem.ipDetails.country_code],
    ["Country Name", dataItem.ipDetails.country_name],
    ["Region Name", dataItem.ipDetails.region_name],
    ["City Name", dataItem.ipDetails.city_name],
    ["Latitude ", dataItem.ipDetails.latitude],
    ["Longitude", dataItem.ipDetails.longitude],
    ["Zip Code", dataItem.ipDetails.zip_code],
    ["Time Zone ", dataItem.ipDetails.time_zone],
    ["ISP ", dataItem.ipDetails.isp],
    ["Domain", dataItem.ipDetails.domain],
    ["Net speed", dataItem.ipDetails.net_speed],
    ["Idd code ", dataItem.ipDetails.idd_code],
    ["Area code ", dataItem.ipDetails.area_code],
    [
      "Weather station code",
      dataItem.ipDetails.weather_station_code,
    ],
    [
      "Weather station name",
      dataItem.ipDetails.weather_station_name,
    ],
    ["MCC", dataItem.ipDetails.mcc],
    ["MNC", dataItem.ipDetails.mnc],
    ["Elevation", dataItem.ipDetails.elevation],
    ["Usage type ", dataItem.ipDetails.usage_type],
    ["Address type", dataItem.ipDetails.address_type],
    ["Category", dataItem.ipDetails.category],
    ["Category name", dataItem.ipDetails.category_name],
    ["Credits Consumed ", dataItem.ipDetails.credits_consumed],
  ];
  doc.autoTable({
    columns: columns,
    body: data,
    margin: { left: xPosition },
    startY: yPosition,
    tableWidth: maxWidth,

    styles: {
      fillColor: [220, 220, 220],
    },
    headStyles: {
      fillColor: [50, 50, 50],
      fontSize: 14,
    },
    bodyStyles: {
      fillColor: [220, 220, 220],
      fontSize: 12,
    },
  });
  doc.setPage(4);
  yPosition = pageHeight / 5 - 12;
  doc.setFontSize(24);
  doc.setFont("Barlow", "bold");
  doc.text("Technical Analysis", xPosition, yPosition);
  yPosition += 18;
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  text = `Based on the investigation into the device associated with the IP address ${dataItem.ip}, the following information has been identified:`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 16;
  text = `The IP address is located in ${dataItem.ipDetails.city_name}, ${dataItem.ipDetails.region_name}, ${dataItem.country}, with coordinates ${dataItem.ipDetails.latitude}° N latitude and ${dataItem.ipDetails.longitude}° E longitude. The timezone for this location is ${dataItem.ipDetails.city_name}. The zip code for this area is ${dataItem.ipDetails.zip_code}, and the time zone is ${dataItem.ipDetails.time_zone}.`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 16;
  text = `The device associated with this IP address utilizes the Chrome browser version 115.0.0.0 on the Windows 10 operating system. The user agent string reveals the Device information as ${dataItem.host}.`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 16;
  text = `The device is connected to the domain ${dataItem.ipDetails.domain}. The net speed is categorized as ${dataItem.ipDetails.category}. The IDD code is ${dataItem.ipDetails.idd_code}, and the area code is ${dataItem.ipDetails.area_code}.`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 16;
  text = ``;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  doc.setPage(5);
  yPosition = pageHeight / 5 - 12;
  text = `The findings suggest that the device associated with the IP address ${dataItem.ip} is located in ${dataItem.ipDetails.city_name}, ${dataItem.country}  and is linked to ${dataItem.ipDetails.domain}. It is categorized as a device in a data center and falls under the Data Centers category. By analyzing the IP address, device details, and associated data, you can take several steps to assist in your efforts to get your money back. Here's how this information can be used:`;
  doc.setFontSize(12);
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 18;
  doc.setFontSize(20);
  doc.setFont("Barlow", "bold");
  doc.text("Contact Law Enforcement Authorities", xPosition, yPosition);
  yPosition += 18;
  text = `Based on the IP address, which indicates the location of the device used in the scam as ${dataItem.ipDetails.city_name}, ${dataItem.ipDetails.region_name}, ${dataItem.country}, you should promptly file a complaint with the appropriate law enforcement agency in that jurisdiction. Specifically, contact the ${dataItem.ipDetails.city_name} Police Service and provide them with a detailed report of the scam. Your complaint should consist a chronological account of the scam events and the interactions with the perpetrator(s), the Online Justice's full report contains your IP address, the device details, the location data, and the other technical evidence, including any additional evidence you may have (emails, messages, financial transaction details etc.) You should include your identity and contact information as the victim in your report.`;
  doc.setFont("Barlow", "light");
  doc.setFontSize(12);
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  doc.setPage(6);
  yPosition = pageHeight / 5 - 12;
  text = `You can file a report online at the ${dataItem.ipDetails.city_name} Police Service website or by visiting your local division in person. Cooperate fully with the investigator(s) assigned to your case and provide any further information or evidence requested promptly.`;
  doc.setFont("Barlow", "light");
  doc.setFontSize(12);
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  text =
    "Obtain an official case/incident number and the contact information of the investigator(s) handling your report for future reference.";
  doc.setFont("Barlow", "light");
  doc.setFontSize(12);
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 18;
  doc.setFontSize(20);
  doc.setFont("Barlow", "bold");
  doc.text("Collaborate with Cybersecurity Experts", xPosition, yPosition);
  yPosition += 18;
  text = `Consult with cybersecurity professionals who specialize in cyber fraud, scams, and online crimes. Depending on the specifics of your case, it may be wise to consult with seasoned cyber security experts to help you figure out what the best legal strategy is. They may recommend additional steps to secure your personal information, mitigate any ongoing risks, and increase the chances of recovering your funds. Cybersecurity experts are able to deeply investigate this data using specific instruments and more developed means apart from those known traditionally. This can help to identify criminal's digital traces, connect him with any wrong doing as well as provide more proof to back up the criminal case at hand.`;
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 18;
  doc.setPage(7);
  yPosition = pageHeight / 5 - 12;
  doc.setFontSize(20);
  doc.setFont("Barlow", "bold");
  doc.text("Seek Legal Assistance", xPosition, yPosition);
  yPosition += 18;
  text = `Given the complexity of cybercrime cases and the potential legal, ramifications, it is crucial to engage the services of a reputable legal professional with specific expertise in handling cybercrimes, fraud, and online offenses. Provide them with the comprehensive report from Online Justice, including the IP address, device information, location data, and any other relevant evidence you have gathered.`;
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 18;
  text = `A knowledgeable legal professional who specializes in cyber crimes may provide you with a lot of assistance on what legal choices are accessible to you, be it civil or criminal. They will evaluate the weaknesses and strengths of your case, give you some feedback about the possibility of the case being won, and suggest the best option for your particular situation. They can initiate proceedings against the perpetrator(s) on your behalf and support you throughout the process of recovering your funds.`;
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  doc.setPage(8);
  yPosition = pageHeight / 5 - 12;
  doc.setFontSize(20);
  doc.setFont("Barlow", "bold");
  doc.text("Coordinate with the ISP", xPosition, yPosition);
  yPosition += 18;
  text = `The ISP (Internet Service Provider) associated with the IP address, Microsoft Corporation in this case, can be contacted to report the scammer's activities. It is a good idea to contact the ISP about what the scammer did, show the ISP the collected proof, comprising IP address, device details, physical address and a summary of the fraudulent events. There are teams in place at the nternet Service Providers that are focused on these issues, and which have specific procedures set up to handle complaints regarding illegal activities carried out over their networks. One way of starting off this procedure is through reporting that criminal's IP address so as to allow them conduct their inhouse operational systems geared towards recognizing the individual user behind such IP address for purpose of suspending or stopping the account said user has been engaged in fraudulent activities.The ISP may collaborate with law enforcement and provide additional information that can aid in identifying the scammer.`;
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 18;
  doc.setFontSize(20);
  doc.setFont("Barlow", "bold");
  doc.text("Preserve Evidence", xPosition, yPosition);
  yPosition += 18;
  text = "Ensure that you keep a record of all communications, ";
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  doc.setPage(9);
  yPosition = pageHeight / 5 - 12;
  yPosition += 6;
  text =
    "transactions, and any other evidence related to the scam. This includes emails, chat logs, screenshots, and financial transaction details. Preserve this evidence as it may be crucial for investigations and legal proceedings.";
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  text = "To effectively preserve evidence, follow these best practices:";
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  text =
    "- Create backup copies of all relevant data on secure external storage devices or cloud storage services.";
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  text =
    "- Do not alter or edit any original files, as maintaining their integrity is crucial for legal proceedings.";
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  text =
    "- Document the date, time, and circumstances support@onlinejustice.com surrounding each piece of evidence collected.";
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  text =
    "It's important to note that pursuing scammers and recovering funds can be complex and time-consuming. It is recommended to work closely with professionals who specialize in fraud investigations and legal matters. They can guide you through the process, provide support, and increase the chances of a successful outcome.";
  doc.setFontSize(12);
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  text =
    "Remember to exercise caution and avoid taking any unauthorized actions that may compromise your safety or hinder the investigation.";
  doc.setFontSize(12);
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  doc.setPage(10);
  yPosition = pageHeight / 5 - 12;
  doc.setFontSize(20);
  doc.setFont("Barlow", "bold");
  doc.text("Disclaimer", xPosition, yPosition);
  yPosition += 18;
  text =
    "This report is based on the information available at the time of analysis and should not be considered as definitive proof or conclusive evidence. The IP addresses and device information are subject to change as they may be masked or manipulated by the perpetrators. Further investigation is required to uncover additional details and determine the full extent of the scam";
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  text =
    "This information is provided for general guidance only and does not constitute legal advice. Consult qualified legal counsel and cybercrime experts to receive advice specific to your situation. Online Justice and I assume no liability for damages arising from actions based on this guidance.";
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  text =
    "If you have any questions or require further assistance, please feel free to contact us";
  doc.setFontSize(12);
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  doc.text("Sincerely,", xPosition, yPosition);
  yPosition += 6;
  doc.text("Investigation Analyst", xPosition, yPosition);
  yPosition += 6;
  doc.text("OnlineJustice", xPosition, yPosition);

  return doc;
};

export const downloadTrackMasterPDF = (options: GeneratePdfOptions): void => {
  const doc = generateTrackMasterPDF(options);
  doc.save(`${options.sessionId}.pdf`);
};

export const getTrackMasterPDFBlob = async (options: GeneratePdfOptions): Promise<Blob> => {
  const doc = generateTrackMasterPDF(options);
  return doc.output('blob');
};

// Generate PDF for multiple entries
export const generateTrackMasterMultiPDF = ({
  dataItems,
  clientFullName,
  clientEmail,
}: Omit<GenerateMultiPdfOptions, 'sessionId'>): jsPDF => {
  let maxWidth = 320;
  let text = "";

  const pageImage = new Image();
  pageImage.src = PageImageSrc;
  const brandPage = new Image();
  brandPage.src = BrandImageSrc;

  applyPlugin(jsPDF);
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const barlowLight = BarlowLight;
  const barlowBold = BarlowBold;
  doc.addFont(barlowLight, "Barlow", "light");
  doc.addFont(barlowBold, "Barlow", "bold");
  let yPosition = pageHeight / 5;
  const xPosition = 25;

  // Add header/footer to first page
  doc.addImage(pageImage, pageWidth - 60, 0, 225 / 3, 389 / 3);
  doc.addImage(brandPage, 20, 15, 829 / 18, 332 / 18);
  text = "support@onlinejustice.com";
  const text_width = doc.getTextWidth(text);
  doc.setFont("Barlow", "light");
  doc.text(text, xPosition, pageHeight - 12);
  doc.setDrawColor(211, 167, 108);
  doc.line(
    text_width + xPosition + 12,
    pageHeight - 12,
    pageWidth,
    pageHeight - 12
  );

  // Cover page
  doc.setFont("Barlow", "light");
  maxWidth = 120;
  text =
    `This comprehensive report contains information about ${dataItems.length} device${dataItems.length > 1 ? 's' : ''} used by the scammer, including IP addresses, device information, timezones, and location details. This information can be used to identify the scammer and potentially recover the funds.`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 10;
  doc.setFontSize(24);
  doc.setFont("Barlow", "bold");
  doc.text("Report: Scam Investigation Findings", xPosition, yPosition + 20);
  yPosition += 18;
  doc.setFontSize(20);
  doc.text(`for ${clientFullName || clientEmail || "Client"}`, xPosition, yPosition + 12);
  yPosition += 12;
  doc.setFontSize(14);
  doc.setFont("Barlow", "light");
  doc.text(`Total Tracked Devices: ${dataItems.length}`, xPosition, yPosition + 12);

  // Introduction page
  doc.addPage();
  doc.addImage(pageImage, pageWidth - 60, 0, 225 / 3, 389 / 3);
  doc.addImage(brandPage, 20, 15, 829 / 18, 332 / 18);
  doc.text(text, xPosition, pageHeight - 12);
  doc.line(
    text_width + xPosition + 12,
    pageHeight - 12,
    pageWidth,
    pageHeight - 12
  );

  doc.setFontSize(24);
  yPosition = pageHeight / 5 - 12;
  doc.setFont("Barlow", "bold");
  doc.text("Introduction", xPosition, yPosition);
  yPosition += 18;
  doc.setFontSize(14);
  doc.setFont("Barlow", "light");
  text = `This report presents the findings of the investigation into a scam targeting ${clientFullName || clientEmail || "the client"}. The investigation has identified ${dataItems.length} device access${dataItems.length > 1 ? 'es' : ''} involved in the fraudulent activity. The following report provides a detailed analysis of the available data and technical information obtained from the IP addresses used during the fraudulent activity.`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);

  // Summary of all devices
  yPosition += 24;
  doc.setFontSize(20);
  doc.setFont("Barlow", "bold");
  doc.text("Devices Summary", xPosition, yPosition);
  yPosition += 12;

  const uniqueIPs = [...new Set(dataItems.map(item => item.ip))];
  const uniqueCountries = [...new Set(dataItems.map(item => item.country))];
  const uniqueISPs = [...new Set(dataItems.map(item => item.ipDetails.isp))];

  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  text = `Total Device Accesses: ${dataItems.length}`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  text = `Unique IP Addresses: ${uniqueIPs.length}`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  text = `Countries Detected: ${uniqueCountries.join(", ")}`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  text = `Internet Service Providers: ${uniqueISPs.length}`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);

  // Detailed information for each device
  dataItems.forEach((dataItem, index) => {
    doc.addPage();
    doc.addImage(pageImage, pageWidth - 60, 0, 225 / 3, 389 / 3);
    doc.addImage(brandPage, 20, 15, 829 / 18, 332 / 18);
    doc.setFont("Barlow", "light");
    doc.text(text, xPosition, pageHeight - 12);
    doc.line(
      text_width + xPosition + 12,
      pageHeight - 12,
      pageWidth,
      pageHeight - 12
    );

    yPosition = pageHeight / 5 - 20;
    doc.setFontSize(20);
    doc.setFont("Barlow", "bold");
    doc.text(`Device #${index + 1} - Investigation Details`, xPosition, yPosition);
    yPosition += 18;

    doc.setFontSize(14);
    doc.setFont("Barlow", "light");
    doc.text("Device Information", xPosition, yPosition);
    yPosition += 12;

    const columns = ["Information", "Value"];
    let data = [
      ["IP address", dataItem.ip],
      ["Device", dataItem.host],
    ];

    doc.autoTable({
      columns: columns,
      body: data,
      margin: { left: xPosition },
      startY: yPosition,
      tableWidth: maxWidth,
      styles: {
        fillColor: [220, 220, 220],
      },
      headStyles: {
        fillColor: [50, 50, 50],
        fontSize: 14,
      },
      bodyStyles: {
        fillColor: [220, 220, 220],
        fontSize: 12,
      },
    });

    // Location Information
    doc.addPage();
    doc.addImage(pageImage, pageWidth - 60, 0, 225 / 3, 389 / 3);
    doc.addImage(brandPage, 20, 15, 829 / 18, 332 / 18);
    doc.setFont("Barlow", "light");
    doc.text(text, xPosition, pageHeight - 12);
    doc.line(
      text_width + xPosition + 12,
      pageHeight - 12,
      pageWidth,
      pageHeight - 12
    );

    yPosition = pageHeight / 5 - 20;
    doc.setFontSize(14);
    doc.setFont("Barlow", "bold");
    doc.text(`Device #${index + 1} - Location Information`, xPosition, yPosition);
    yPosition += 12;

    data = [
      ["Response", dataItem.ipDetails.response],
      ["Country Code", dataItem.ipDetails.country_code],
      ["Country Name", dataItem.ipDetails.country_name],
      ["Region Name", dataItem.ipDetails.region_name],
      ["City Name", dataItem.ipDetails.city_name],
      ["Latitude ", dataItem.ipDetails.latitude],
      ["Longitude", dataItem.ipDetails.longitude],
      ["Zip Code", dataItem.ipDetails.zip_code],
      ["Time Zone ", dataItem.ipDetails.time_zone],
      ["ISP ", dataItem.ipDetails.isp],
      ["Domain", dataItem.ipDetails.domain],
      ["Net speed", dataItem.ipDetails.net_speed],
      ["Idd code ", dataItem.ipDetails.idd_code],
      ["Area code ", dataItem.ipDetails.area_code],
      ["Weather station code", dataItem.ipDetails.weather_station_code],
      ["Weather station name", dataItem.ipDetails.weather_station_name],
      ["MCC", dataItem.ipDetails.mcc],
      ["MNC", dataItem.ipDetails.mnc],
      ["Elevation", dataItem.ipDetails.elevation],
      ["Usage type ", dataItem.ipDetails.usage_type],
      ["Address type", dataItem.ipDetails.address_type],
      ["Category", dataItem.ipDetails.category],
      ["Category name", dataItem.ipDetails.category_name],
      ["Credits Consumed ", dataItem.ipDetails.credits_consumed],
    ];

    doc.autoTable({
      columns: columns,
      body: data,
      margin: { left: xPosition },
      startY: yPosition,
      tableWidth: maxWidth,
      styles: {
        fillColor: [220, 220, 220],
      },
      headStyles: {
        fillColor: [50, 50, 50],
        fontSize: 14,
      },
      bodyStyles: {
        fillColor: [220, 220, 220],
        fontSize: 12,
      },
    });
  });

  // Add general recommendations pages (similar to single PDF)
  doc.addPage();
  doc.addImage(pageImage, pageWidth - 60, 0, 225 / 3, 389 / 3);
  doc.addImage(brandPage, 20, 15, 829 / 18, 332 / 18);
  doc.setFont("Barlow", "light");
  doc.text(text, xPosition, pageHeight - 12);
  doc.line(
    text_width + xPosition + 12,
    pageHeight - 12,
    pageWidth,
    pageHeight - 12
  );

  yPosition = pageHeight / 5 - 12;
  doc.setFontSize(20);
  doc.setFont("Barlow", "bold");
  doc.text("Recommendations", xPosition, yPosition);
  yPosition += 18;

  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  text = `Based on the investigation of ${dataItems.length} device access${dataItems.length > 1 ? 'es' : ''}, the following recommendations are provided to assist in recovering your funds and pursuing legal action.`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 18;

  doc.setFontSize(16);
  doc.setFont("Barlow", "bold");
  doc.text("1. Contact Law Enforcement", xPosition, yPosition);
  yPosition += 12;
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  text = `File a comprehensive report with law enforcement in the jurisdictions where the devices were detected: ${uniqueCountries.join(", ")}. Provide them with this complete report containing all device and location information.`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);

  yPosition += 18;
  doc.setFontSize(16);
  doc.setFont("Barlow", "bold");
  doc.text("2. Seek Legal Assistance", xPosition, yPosition);
  yPosition += 12;
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  text = `Engage a legal professional specializing in cybercrimes. The multiple device accesses documented in this report provide strong evidence for legal proceedings.`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);

  yPosition += 18;
  doc.setFontSize(16);
  doc.setFont("Barlow", "bold");
  doc.text("3. Coordinate with ISPs", xPosition, yPosition);
  yPosition += 12;
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  text = `Contact the Internet Service Providers identified in this report: ${uniqueISPs.slice(0, 3).join(", ")}${uniqueISPs.length > 3 ? ' and others' : ''}. Report the fraudulent activities and request their cooperation in the investigation.`;
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);

  // Disclaimer
  doc.addPage();
  doc.addImage(pageImage, pageWidth - 60, 0, 225 / 3, 389 / 3);
  doc.addImage(brandPage, 20, 15, 829 / 18, 332 / 18);
  doc.setFont("Barlow", "light");
  doc.text(text, xPosition, pageHeight - 12);
  doc.line(
    text_width + xPosition + 12,
    pageHeight - 12,
    pageWidth,
    pageHeight - 12
  );

  yPosition = pageHeight / 5 - 12;
  doc.setFontSize(20);
  doc.setFont("Barlow", "bold");
  doc.text("Disclaimer", xPosition, yPosition);
  yPosition += 18;
  text =
    "This report is based on the information available at the time of analysis and should not be considered as definitive proof or conclusive evidence. The IP addresses and device information are subject to change as they may be masked or manipulated by the perpetrators. Further investigation is required to uncover additional details and determine the full extent of the scam.";
  doc.setFontSize(12);
  doc.setFont("Barlow", "light");
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  text =
    "This information is provided for general guidance only and does not constitute legal advice. Consult qualified legal counsel and cybercrime experts to receive advice specific to your situation. Online Justice assumes no liability for damages arising from actions based on this guidance.";
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  text =
    "If you have any questions or require further assistance, please feel free to contact us";
  yPosition = justifyText(doc, text, xPosition, yPosition, maxWidth);
  yPosition += 6;
  doc.text("Sincerely,", xPosition, yPosition);
  yPosition += 6;
  doc.text("Investigation Analyst", xPosition, yPosition);
  yPosition += 6;
  doc.text("OnlineJustice", xPosition, yPosition);

  return doc;
};

export const downloadTrackMasterMultiPDF = (options: GenerateMultiPdfOptions): void => {
  const doc = generateTrackMasterMultiPDF(options);
  doc.save(`${options.sessionId}_comprehensive.pdf`);
};

export const getTrackMasterMultiPDFBlob = async (options: GenerateMultiPdfOptions): Promise<Blob> => {
  const doc = generateTrackMasterMultiPDF(options);
  return doc.output('blob');
};
